// MessageBubbleRedux.tsx
import React, { ReactNode, useState, useCallback, useRef, useEffect } from 'react';
import { Anchorme } from "react-anchorme";
import moment from 'moment';
import { ChevronDown, Reply as ReplyIcon, Smile as ReactIcon, FileText, Phone, ExternalLink, X, Check, CheckCheck } from 'lucide-react';
import logger, { Logger } from '../../../utils/logger';

// Redux imports
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import {
  selectCurrentUser,
  selectWhatsappIntegration
} from '../../../store/slices/userSlice';
import {
  Message,
  sendReaction,
  setReplyContext
} from '../../../store/slices/messagesSlice';
import {
  openMediaPreview,
  selectIsMediaCached,
  selectMediaCacheItem,
  downloadMedia
} from '../../../store/slices/mediaSlice';
import { store } from '../../../store/store';

// Component imports
import { MessageMedia } from './MessageMedia';

// Utility imports
import { 
  extractMessageContent, 
  formatMessageTime, 
  isOutgoingMessage, 
  isMediaMessage,
  getMimeTypeFromExtension
} from '../utils/messageFormatterUtil';
import toast from 'react-hot-toast';

interface MessageBubbleProps {
  message: Message;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
  showTimestamp?: boolean;
}

export const MessageBubbleRedux: React.FC<MessageBubbleProps> = ({
  message,
  isFirstInGroup = true,
  isLastInGroup = true,
  showTimestamp = true,
}) => {
  const dispatch = useAppDispatch();
  
  

  // Redux state
  const currentUser = useAppSelector(selectCurrentUser);
  const whatsappIntegration = useAppSelector(selectWhatsappIntegration);
  const isMediaCached = useAppSelector(selectIsMediaCached(message._id));
  const cachedMedia = useAppSelector(isMediaCached ? 
    selectMediaCacheItem(message._id) : 
    () => null
  );
  
  // Local UI state
  const [showEmojiBar, setShowEmojiBar] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isMessageExpanded, setIsMessageExpanded] = useState(false);

  
  // Determine if message is outgoing (sent by current user)
  const isOutgoing = isOutgoingMessage(message);
  
  // Quick emoji options for reactions
  const quickEmojis = ['👍', '❤️', '😂', '😮', '😢', '😭'];

  


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setShowEmojiBar(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Format message content for display
 // NEW formatContent function with auto-collapse
const formatContent = useCallback((text: any): ReactNode[] => {
  console.log('[formatContent] Processing message:', message._id);
  
  // Extract message text using existing utility
  const messageText = extractMessageContent(text);
  console.log('[formatContent] Extracted text length:', messageText.length);
  
  // Simple character limit (adjust as needed)
  const CHARACTER_LIMIT = 150;
  const isLongMessage = messageText.length > CHARACTER_LIMIT;
  
  console.log('[formatContent] Message analysis:', {
    messageId: message._id,
    totalLength: messageText.length,
    characterLimit: CHARACTER_LIMIT,
    isLongMessage,
    isCurrentlyExpanded: isMessageExpanded
  });
  
  // Determine display text
  let displayText = messageText;
  
  if (isLongMessage && !isMessageExpanded) {
    // Smart truncation at word boundary
    let truncateAt = CHARACTER_LIMIT;
    console.log('[formatContent] Finding word boundary for truncation...');
    
    // Find last space or newline within limit
    while (truncateAt > 0 && messageText[truncateAt] !== ' ' && messageText[truncateAt] !== '\n') {
      truncateAt--;
    }
    
    // Fallback to character limit if no boundary found
    if (truncateAt === 0) {
      truncateAt = CHARACTER_LIMIT;
      console.log('[formatContent] No word boundary found, using character limit');
    } else {
      console.log('[formatContent] Found word boundary at position:', truncateAt);
    }
    
    displayText = messageText.substring(0, truncateAt).trim();
    console.log('[formatContent] Truncated from', messageText.length, 'to', displayText.length, 'characters');
  }
  
  // Format the text (preserve existing Anchorme logic)
  const formattedContent = displayText.split('\n').map((line: string, i: number, arr: string[]) => (
    <React.Fragment key={i}>
      <Anchorme target="_blank">
        {line || ' '}
      </Anchorme>
      {i < arr.length - 1 && <br />}
    </React.Fragment>
  ));
  
  // Add "read more" button if needed
  if (isLongMessage && !isMessageExpanded) {
    console.log('[formatContent] Adding read more button for message:', message._id);
    
    formattedContent.push(
      <React.Fragment key="read-more">
        <br />
        <button
          className="read-more-btn"
          onClick={(e) => {
            console.log('[formatContent] Read more clicked! Message:', message._id);
            console.log('[formatContent] Expanding message from', displayText.length, 'to', messageText.length, 'characters');
            e.preventDefault();
            e.stopPropagation();
            setIsMessageExpanded(true);
          }}
        >
          read more
        </button>
      </React.Fragment>
    );
  } else if (isLongMessage && isMessageExpanded) {
    console.log('[formatContent] Message is expanded, showing full text:', messageText.length, 'characters');
  }
  
  return formattedContent;
}, [isMessageExpanded, message._id]); // IMPORTANT: Add dependencies

  // Handle emoji reactions
  const handleEmojiClick = useCallback((emoji: string) => {
    if (message.messageId || message._id) {
     
      
      dispatch(sendReaction({
        messageId: message.messageId || message._id,
        emoji,
        phone: message.phone || '' // This should be available in your message object
      }));
    }
    
    setShowEmojiBar(false);
    setShowDropdown(false);
  }, [dispatch, message]);
  
  // Handle reply
  const handleReplyClick = useCallback(() => {

    dispatch(setReplyContext(message));
    setShowDropdown(false);
  }, [dispatch, message]);

  // Handle media download

const handleMediaDownload = useCallback((): Promise<string | null> => {
  return new Promise<string | null>((resolve, reject) => {
    if (message._id && isMediaMessage(message)) {
     
      
      // Check if already cached before downloading
      if (isMediaCached && cachedMedia?.url) {
      
        resolve(cachedMedia.url);
        return;
      }
      
      dispatch(downloadMedia(message))
        .unwrap()
        .then(() => {
        
          // Get the updated cache after download
          const state = store.getState();
          const updatedMedia = selectMediaCacheItem(message._id)(state);
          
          if (updatedMedia?.url) {
            resolve(updatedMedia.url);
          } else {
           
            resolve(null);
          }
        })
        .catch((error) => {
         
          reject(error);
        });
    } else {
    
      resolve(null);
    }
  });
}, [dispatch, message, isMediaCached, cachedMedia]);

  const handleMediaPreview = useCallback(() => {
    if (message._id) {
      if (isMediaCached) {
        // If already cached, open preview immediately
       
        dispatch(openMediaPreview(message._id));
      } else {
        // If not cached, download first then open preview
        
        handleMediaDownload()
          .then(() => {
            // Add a delay to ensure Redux state is updated
            setTimeout(() => {
            
              dispatch(openMediaPreview(message._id));
            }, 100);
          })
          .catch(error => {
          
            toast.error("Failed to load media preview");
          });
      }
    }
  }, [dispatch, message._id, isMediaCached, handleMediaDownload]);

const renderContent = useCallback((): ReactNode => {
 


  // For reaction messages
  if (message?.messageType === "reaction") {
    return formatContent(message.message.emoji || '');
  }


// For template messages - render them in WhatsApp style
if (message?.messageType === "template") {
  logger.log("Rendering template message:", message._id);
  
  return (
    <div className="template-message-container">
      {/* Document header */}
      {!Array.isArray(message.message?.header) && 
       message.message?.header && 
       'parameters' in message.message.header && 
       message.message.header.parameters?.[0]?.type === "document" && (
        <div className="document-header">
          <div className="document-icon">
            <i className="fa-regular fa-file-pdf"></i>
          </div>
          <div className="document-name">
            {(message.message.header as { 
              parameters?: Array<{ 
                type: string; 
                document?: { link: string; filename: string; } 
              }> 
            }).parameters?.[0]?.document?.filename || "Document.pdf"}
          </div>
          <div className="document-download" 
               onClick={() => {
                 // Type assertion to help TypeScript understand the structure
                 const header = message.message?.header as { 
                   parameters?: Array<{ 
                     type: string; 
                     document?: { link: string; filename: string; } 
                   }> 
                 };
                 const docLink = header.parameters?.[0]?.document?.link;
                 if (docLink) {
                   window.open(docLink, '_blank');
                 }
               }}>
            <i className="fa-regular fa-download"></i>
          </div>
        </div>
      )}
      
      {/* Text header */}
      {Array.isArray(message.message?.header) && message.message.header.length > 0 && (
        <div className="text-header">
          {message.message.header[0]?.text}
        </div>
      )}
      
      {/* Message body */}
      {typeof message.message?.body === "object" && 
       message.message.body?.text?.body && (
        <div className="template-body">
          {formatContent(message.message.body.text.body)}
        </div>
      )}
      
      {/* Footer text */}
      {Array.isArray(message.message?.footer) && message.message.footer.length > 0 && (
        <div className="template-footer">
          {message.message.footer.map((footer: any, index: number) => (
            <div key={`footer-${index}`} className="footer-text">
              {footer.text}
            </div>
          ))}
        </div>
      )}
      
      {/* WhatsApp-style buttons */}
      {Array.isArray(message.message?.buttons) && message.message.buttons.length > 0 && (
        <div className="whatsapp-buttons-container">
          {message.message.buttons.map((buttonGroup: any, groupIndex: number) => (
            buttonGroup.buttons?.map((button: any, btnIndex: number) => {
             
              
              if (button.type === "PHONE_NUMBER") {
                return (
                  <a 
                    key={`btn-${btnIndex}`}
                    href={`tel:${button.phone_number}`}
                    className="whatsapp-button phone-button"
                  >
                    {button.text}
                  </a>
                );
              }
              
              if (button.type === "URL") {
                return (
                  <a 
                    key={`btn-${btnIndex}`}
                    href={button.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whatsapp-button"
                  >
                    {button.text}
                  </a>
                );
              }
              
              return null;
            })
          ))}
        </div>
      )}
    </div>
  );
}

  // For media messages
  if (isMediaMessage(message)) {
   
    
    const mediaMessage = {
      _id: message._id,
      messageType: message.messageType as 'video' | 'image' | 'document' | 'audio',
      downloadUrl: cachedMedia?.url || message.downloadUrl,
      message: {
        body: extractMessageContent(message.message),
        id: message.message?.id,
        mime_type: message.message?.mime_type,
        filename: message.message?.filename || 'file' // Provide default filename
      }
    };
  
    return (
      <MessageMedia 
        message={mediaMessage}
        onDownload={handleMediaDownload}
        onPreview={handleMediaPreview}
        integrationInfo={whatsappIntegration}
      />
    );
  }

  // For regular text messages
  return (
    <div className="message-text-content">
      {formatContent(message.message)}
    </div>
  );
}, [message, formatContent, cachedMedia, handleMediaDownload, handleMediaPreview, whatsappIntegration]);

return (
  <div 
    className={`message ${isOutgoing ? 'outgoing' : 'incoming'}
      ${isFirstInGroup ? 'first-in-group' : ''} 
      ${isLastInGroup ? 'last-in-group' : ''}`}
    style={{ position: 'relative' }}  // Ensure parent has relative positioning
  >
    <div className="message-container">
      {isFirstInGroup && isOutgoing && (
        <div className="message-sender">
          <span>{currentUser?.firstName || 'You'}</span>
        </div>
      )}
      
      <div className="message-content">
        {renderContent()}
        
        {isLastInGroup && showTimestamp && (
          <div className="message-footer">
             
   
            <span className="message-time">
              {formatMessageTime(message.createdAt)}
            </span>
            




  {isOutgoing && isLastInGroup && showTimestamp && (() => {
  const getLatestStatus = () => {
    if (message.statusLogs?.length) {
      const sorted = [...message.statusLogs].sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      return sorted[0].status;
    }
    return message.status || 'unknown';
  };
  
  const status = getLatestStatus();
  
  return (
    <div className="message-status" style={{
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      marginTop: '2px'
    }}>
      {status === "failed" && <X size={14} style={{color: '#ffffff'}} />}
      {status === "sent" && <Check size={14} style={{color: '#ffffff'}} />}
      {status === "delivered" && <CheckCheck size={14} style={{color: '#ffffff'}} />}
      {status === "read" && <CheckCheck size={14} style={{color: '#00ff00'}} />}
    </div>
  );
})()}
          </div>
        )}
      </div>

      {/* Dropdown trigger button - positioned outside the bubble 
      {!isOutgoing && (
        <button 
          className="message-dropdown-trigger"
          style={{
            position: 'absolute',
            right: '-28px',  // Position outside the bubble
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#f0f2f5',
            border: 'none',
            padding: '4px',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            opacity: '0',
            zIndex: 2,
            transition: 'opacity 0.2s'
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowDropdown(!showDropdown);
          }}
        >
          <ChevronDown size={16} />
        </button>
      )}*/}

      {/* Dropdown menu - positioned outside the bubble 
      {showDropdown && !isOutgoing && (
        <div ref={menuRef} className="message-dropdown-menu" style={{
          position: 'absolute',
          right: '-135px',  // Position outside bubble with enough space
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'white',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          borderRadius: '8px',
          zIndex: 3,
          padding: '8px 0',
          minWidth: '120px'
        }}>
          <button 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              width: '100%',
              border: 'none',
              background: 'transparent',
              textAlign: 'left',
              cursor: 'pointer'
            }}
            onClick={handleReplyClick}
          >
            <ReplyIcon size={16} /> Reply
          </button>
          <button 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              width: '100%',
              border: 'none',
              background: 'transparent',
              textAlign: 'left',
              cursor: 'pointer'
            }}
            onClick={() => {
              setShowEmojiBar(true);
              setShowDropdown(false);
            }}
          >
            <ReactIcon size={16} /> React
          </button>
        </div>
      )}*/}

      {/* Emoji reactions bar - positioned above the message with better width/visibility */}
      {showEmojiBar && !isOutgoing && (
        <div ref={menuRef} className="emoji-reaction-bar" style={{
          position: 'absolute',
          left: '0',          // Align with left edge of message
          top: '-45px',       // Move further above the message
          background: 'white',
          borderRadius: '20px',
          padding: '6px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          display: 'flex',
          zIndex: 3,
          width: 'auto',      // Let it grow based on content
          minWidth: '240px'   // Ensure minimum width for all emojis
        }}>
          {quickEmojis.map((emoji, index) => (
            <button
              key={index}
              className="emoji-option"
              style={{
                background: 'transparent',
                border: 'none',
                borderRadius: '50%',
                padding: '6px',
                cursor: 'pointer',
                fontSize: '18px',
                flex: '1'      // Make emojis take equal space
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleEmojiClick(emoji);
                setShowEmojiBar(false);
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  </div>
);
};